import { CloseCircleOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
import {
  Company,
  ErrorResponse,
  GET_COMPANY,
  PATCH_COMPANY,
  UploadedFile,
} from '@frontend/data-access';
import { App, Button, Form } from 'antd';
import useTranslation from 'next-translate/useTranslation';
import { useState } from 'react';

import FileUpload, {
  supportedDocumentFormats,
} from '../../../file-upload/file-upload';

interface FormValues {
  deck: UploadedFile;
}

function EditInformationDeckForm({
  companyId,
  onFinish,
  onCancel,
}: {
  companyId?: number;
  onFinish: () => void;
  onCancel: () => void;
}) {
  const { notification } = App.useApp();
  const { t } = useTranslation('common');
  const [isIncomplete, setIsIncomplete] = useState(false);
  const [form] = Form.useForm();
  const { data } = useQuery<{ company: Company }>(GET_COMPANY, {
    variables: { id: companyId },
  });

  const handleCompleted = () => {
    notification.success({
      message: t('successfully_saved'),
    });
    onFinish();
  };

  const handleError = (data: ErrorResponse) => {
    notification.error({
      message: t('error'),
      description: data.message,
      icon: (
        <span className="text-red-600">
          <CloseCircleOutlined />
        </span>
      ),
    });
  };

  const [patchCompany, { loading: loadingPatch }] = useMutation(PATCH_COMPANY, {
    onCompleted: handleCompleted,
    onError: handleError,
  });

  const handleOnFinish = async ({ deck }: FormValues) => {
    await patchCompany({
      variables: {
        input: {
          id: companyId,
          documents: {
            onePagers: [...(data?.company?.onePagers ?? []), deck],
          },
        },
      },
    });
  };

  const handleFormChange = () => {
    const hasErrors = form.getFieldsError().some(({ errors }) => errors.length);
    setIsIncomplete(hasErrors);
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleOnFinish}
      onFieldsChange={handleFormChange}
    >
      <Form.Item
        name="deck"
        rules={[{ required: true, message: t('input_information_deck') }]}
        data-test="information-desk-file-input"
      >
        <FileUpload
          id="information-desk-file-input"
          description={<div className="mb-4">{t('click_or_drag_file')}</div>}
          onUploadSuccess={(file) => form.setFieldValue('deck', file)}
          compact={false}
          supportedFileTypes={supportedDocumentFormats}
          maxFileSize={10000000} // 10MB
        />
      </Form.Item>
      <div className="flex justify-end items-center gap-2">
        <Button disabled={loadingPatch} shape="round" onClick={onCancel}>
          {t('cancel')}
        </Button>
        <Button
          htmlType="submit"
          type="primary"
          loading={loadingPatch}
          shape="round"
        >
          {t('save')}
        </Button>
      </div>
    </Form>
  );
}

export default EditInformationDeckForm;
